import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { startCase } from 'lodash';
import type { FC } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { errorToast, isDateGreaterThanToday } from '@/helpers';
import { _isBlank, _isEmpty, _isNil, _isNotBlank, _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import type { Animal, AnimalApiId, AnimalIdentifier, AnimalSex } from '@/model/Animal.model';
import { getSexOptions } from '@/model/Animal.utils';
import type { ISODate } from '@/model/Common.model';
import type { State } from '@/model/State.model';
import type { StudyApiId } from '@/model/Study.model';

import { featuresSelector } from '@/support/Selectors';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { modalAction } from '@/utils/modal';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

const reasonForChangeSelector = createSelector([featuresSelector], (features) => ({
  reasonForChangeEnabled: features?.reason_for_change === true,
}));

import { validAnimalNameLength } from '@/components/Colony/Animals/Animals.utils.tsx';
import DatePickerNative from '@/components/UI/DatePickerNative';
import Button from '@/components/UI/Button';
import FormField from '@/components/UI/FormFields/Latest/FormField';
import Select from '@/components/UI/FormFields/Latest/Select';

import { useEntitySchemaFieldsByType, useRefreshEntitySchemaOnMount } from '@/utils/entitySchema/useEntitySchema';

import { checkStudyForDuplicates } from '../AssignIdentifiers/AssignIdentifiers.utils';

interface EditSubjectProps {
  animal: Animal;
  studyApiId: StudyApiId;
  handleCallback: (message: string) => void;
  closeModal: () => void;
}

interface AnimalUploadFormData {
  name: string;
  dob: ISODate;
  sex: AnimalSex;
  tail: string;
  tag: string;
  donor: string;
  ear: string;
}

interface AnimalUpdatePayload {
  name: string;
  dob: ISODate | null;
  sex: AnimalSex;
  alt_ids: {
    tail: string | null;
    tag: string | null;
    donor: string | null;
    ear: string | null;
  };
  reason_for_change?: string;
}

const idFields: Array<{ label: string; name: AnimalIdentifier; required?: boolean }> = [
  {
    label: 'Name',
    name: 'name',
    required: true,
  },
  {
    label: 'Tail',
    name: 'tail',
  },
  {
    label: 'Ear',
    name: 'ear',
  },
  {
    label: 'Tag',
    name: 'tag',
  },
  {
    label: 'Donor',
    name: 'donor',
  },
];

const EditSubject: FC<EditSubjectProps> = ({ animal, studyApiId, handleCallback, closeModal }) => {
  useRefreshEntitySchemaOnMount();
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const isDobRequired = entityFieldRequiredMap?.dob ?? true;
  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const { reasonForChangeEnabled } = useSelector(reasonForChangeSelector);
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const sexOptions = getSexOptions(unknownSexEnabled);
  const sexDefault = sexOptions.some(({ value }) => value === animal.sex) ? animal.sex : ('' as AnimalSex);

  const formMethods = useForm<AnimalUploadFormData>({
    defaultValues: {
      name: animal.name,
      dob: animal.dob || '',
      sex: sexDefault,
      ...animal.alt_ids,
    },
  });
  const { handleSubmit, control } = formMethods;
  const { invoke: updateAnimal, loading: saving } = useApiHook({
    endpoint: 'PATCH /api/v1/animals/{animalApiId}',
    options: { invokeOnInit: false, onError: { slug: 'edit-subject-update', capture: true, toast: true } },
  });

  const handlePayload = (data: AnimalUploadFormData, reasonForChange?: string): AnimalUpdatePayload => {
    const nullIfBlank = (value: string | null | undefined): string | null => (_isNotBlank(value) ? value : null);
    return {
      name: data.name,
      dob: _isEmpty(data.dob) ? null : data.dob,
      sex: data.sex,
      alt_ids: {
        tail: nullIfBlank(data.tail),
        tag: nullIfBlank(data.tag),
        donor: nullIfBlank(data.donor),
        ear: nullIfBlank(data.ear),
      },
      ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
    };
  };

  const saveAnimal = async (data: AnimalUploadFormData, reasonForChange?: string) => {
    if (_isNil(animal.api_id)) {
      ExceptionHandler.captureException(
        new InVivoError('EditSubject: cannot update animal without an api_id', {
          slug: 'edit-subject-missing-api-id',
        })
      );
      errorToast('Unable to update this animal. Please refresh and try again.');
      return;
    }
    try {
      await updateAnimal({
        path: { animalApiId: animal.api_id },
        body: handlePayload(data, reasonForChange),
      });
    } catch {
      return;
    }
    handleCallback('Successfully updated animal');
    closeModal();
  };

  const onSubmit = async (data: AnimalUploadFormData) => {
    if (reasonForChangeEnabled) {
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Animal information has changed',
        onClick: (reasonForChange?: string) => saveAnimal(data, reasonForChange),
      });
      return;
    }
    await saveAnimal(data);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`center mv3 pa4 bg-white br2 shadow-4`}
      style={{ maxWidth: 420 }}
    >
      <div className="tc pb3">
        <h3 className="normal f4 lh-title">Edit {animal.name}</h3>
      </div>
      <FormProvider {...formMethods}>
        {idFields.map(({ name, label, required }) => (
          <EditSubjectNameField
            key={`${name}-field`}
            name={name}
            label={label}
            studyApiId={studyApiId}
            animalId={animal?.api_id}
            required={required}
          />
        ))}
        <div className="mb3">
          <FormField name="dob" label="Date of birth" required={isDobRequired}>
            <Controller
              name="dob"
              control={control}
              defaultValue={animal.dob || ''}
              rules={{
                validate: (value: string) => {
                  if (_isEmpty(value)) {
                    return !isDobRequired || 'DOB is required';
                  }
                  const selectedDate = new Date(value);
                  return !isDateGreaterThanToday(selectedDate) || 'DOB cannot be in the future';
                },
              }}
              render={({ field: { onChange, value } }) => (
                <DatePickerNative value={value || ''} onChange={onChange} testId="edit-subject-dob-input" />
              )}
            />
          </FormField>
        </div>
        <div className="mb3">
          <Select name="sex" label="Sex" required value={sexDefault} options={sexOptions} testId="edit-subject-sex" />
        </div>
      </FormProvider>
      <div className="pt2">
        <Button submit className="mr3" disabled={saving} testId="edit-subject__save">
          Save
        </Button>
        <Button plain onClick={closeModal}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

interface EditSubjectNameFieldProps {
  name: AnimalIdentifier;
  label: string;
  studyApiId: StudyApiId;
  animalId?: AnimalApiId;
  required?: boolean;
}

const EditSubjectNameField: FC<EditSubjectNameFieldProps> = ({
  name,
  label,
  studyApiId,
  animalId,
  required = false,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<AnimalUploadFormData>();
  const state = useSelector((state: State) => state?.team);
  const rules: { validate?: (value: string) => Promise<boolean | string> } = {};

  if (_isNil(animalId) || _isNil(studyApiId)) {
    return null;
  }

  if (name === 'tag' && state?.features?.assign_identifiers_tag_unique === true) {
    rules.validate = async (value) => {
      if (_isBlank(value)) {
        return true;
      }
      try {
        const result = await checkStudyForDuplicates(value, 'tag', studyApiId, animalId);
        return result ? true : 'This identifier is already in use';
      } catch (error) {
        return 'An error occurred';
      }
    };
  } else {
    rules.validate = async (value: string) => {
      if (required && _isEmpty(value)) {
        return `${startCase(name)} cannot be empty`;
      }
      if (name === 'name' && !validAnimalNameLength(value)) {
        return 'Name cannot be longer than 40 characters.';
      }
      return true;
    };
  }

  return (
    <div className="mb3">
      <label htmlFor={name}>{`${label}${required ? ' (required)' : ''}`}</label>
      <input
        id={name}
        data-testid={`edit-subject-${name}-input`}
        type="text"
        className={cn({ input__error: _notNil(errors[name]) })}
        {...register(name, rules)}
      />
      <ErrorMessage errors={errors} name={name} render={({ message }) => <p className="f6 red lh-copy">{message}</p>} />
    </div>
  );
};

export default EditSubject;
